import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { MaskedLines, Fade } from "@/components/motion";
import ProjectCard from "@/components/ProjectCard";
import EmptyState from "@/components/EmptyState";
import { getCity, CITIES } from "@/data/cities";
import { projectsByCity } from "@/data/projects";

export default function CityPage() {
    const { slug } = useParams();
    const city = getCity(slug);

    if (!city) {
        return (
            <div className="mx-auto max-w-7xl px-5 pb-24 pt-44 sm:px-8" data-testid="city-not-found">
                <p className="eyebrow">Unknown City</p>
                <h1 className="mt-4 font-serif text-4xl text-ivory">
                    We haven't mapped that Sthaan yet.
                </h1>
                <p className="mt-4 max-w-md text-sm text-mist">
                    Explore one of our launch cities instead.
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                    {CITIES.map((c) => (
                        <Link
                            key={c.slug}
                            to={`/city/${c.slug}`}
                            data-testid={`city-notfound-${c.slug}`}
                            className="rounded-full border border-black/10 px-4 py-2 text-sm text-mist transition-colors hover:border-gold/40 hover:text-gold"
                        >
                            {c.name}
                        </Link>
                    ))}
                </div>
            </div>
        );
    }

    const projects = projectsByCity(city.slug);

    return (
        <div data-testid={`city-page-${city.slug}`}>
            <section className="relative flex min-h-[68vh] items-end overflow-hidden">
                <img
                    src={city.image}
                    alt={`${city.name} cityscape`}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-obsidian/25" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-44 sm:px-8">
                    <Fade delay={0.1}>
                        <p className="eyebrow flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5" /> {city.state}
                        </p>
                    </Fade>
                    <h1 className="mt-5 font-serif text-5xl font-light leading-none tracking-tight text-ivory sm:text-6xl lg:text-7xl">
                        <MaskedLines lines={[city.name]} />
                    </h1>
                    <Fade delay={0.7}>
                        <p className="mt-5 max-w-xl text-sm leading-relaxed text-mist sm:text-base">
                            {city.tagline}
                        </p>
                    </Fade>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                {projects.length > 0 ? (
                    <>
                        <p className="eyebrow mb-10">
                            {projects.length} project{projects.length > 1 ? "s" : ""} in{" "}
                            {city.name}
                        </p>
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {projects.map((p, i) => (
                                <ProjectCard key={p.slug} project={p} index={i} />
                            ))}
                        </div>
                    </>
                ) : (
                    <EmptyState citySlug={city.slug} nearby={city.nearby} />
                )}
            </section>
        </div>
    );
}
